/**
 * Header - Site navigation and branding
 * Displays logo, navigation menu, and register button
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLinkedinIn, faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import '../ComponentsStyling/Header.css';

/* Brand and social links */

const SOCIAL_LINKS = {
    linkedin: 'https://www.linkedin.com/company/ai-expo2026/?viewAsMember=true',
    instagram: 'https://www.instagram.com/aiexpo2026?igsh=NTllbmJ5YXZ6dGVm',
};

/* Navigation icons */

const IcoHome = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10.5L11 3l8 7.5V19a1 1 0 01-1 1H4a1 1 0 01-1-1v-8.5z" />
        <path d="M8 20v-7h6v7" />
    </svg>
);
const IcoAbout = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <circle cx="11" cy="8" r="1.2" fill="currentColor" stroke="none" />
        <path d="M11 11.5v5" />
    </svg>
);
const IcoProgram = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="16" height="16" rx="1.5" />
        <path d="M3 9h16M8 2v3M14 2v3" />
        <circle cx="8" cy="13.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="11" cy="13.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="14" cy="13.5" r="1" fill="currentColor" stroke="none" />
    </svg>
);
/* Unused icon component - kept for future reference
const IcoSpeakers = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="7.5" r="3.8" />
        <path d="M3 20a8 8 0 0116 0" />
    </svg>
);
*/
const IcoPartners = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="3.2" />
        <path d="M2 19c0-3.2 2.7-5.5 6-5.5s6 2.3 6 5.5" />
        <path d="M15 5a3.2 3.2 0 010 6.4" />
        <path d="M20 19c0-2.8-2-4.8-5-5.3" />
    </svg>
);
const IcoExhibit = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="16" height="12" rx="1" />
        <path d="M7 9h8M7 12h5" />
        <path d="M3 17l-1 3M19 17l1 3" />
    </svg>
);
const IcoContact = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 5a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
        <path d="M3 7l8 6 8-6" />
    </svg>
);

/* -------------------------------------------------------------------------- */
/*  Menu definitions                                                          */
/* -------------------------------------------------------------------------- */

/* Unused menu - kept for future reference
const aboutMenu = [
    { label: 'Mission & Vision', id: 'mission-vision' },
    { label: 'The Expo Story', id: 'expo-story' },
    { label: 'Objectives', id: 'objectives' },
    { label: 'Press & Media', id: 'press' },
];
*/
const eventMenu = [
    { label: 'Agenda', id: 'agenda', path: '/agenda' },
    { label: 'Workshops', id: 'workshops', path: '/workshops' },
    { label: 'Speakers', id: 'speakers', path: '/speakers' },
    { label: 'Judges', id: 'judges', path: '/judges' },
    { label: 'Expo Day', id: 'expo-day', path: '/expo-day' },
    { label: 'FAQ', id: 'faq', path: '/faq' },
];

const NAV = [
    { key: 'home',     label: 'Home',         Icon: IcoHome,     path: '/',          menu: null,         scrollId: 'home'      },
    { key: 'about',    label: 'About',        Icon: IcoAbout,    path: '/about',     menu: null,         scrollId: 'about'     },
    { key: 'competition', label: 'Competition', Icon: IcoProgram, path: '/program',  menu: null,         scrollId: 'program'   },
    /* Workshops and FAQ live in the Event menu rather than the top bar — listing
       them in both put the same page in the nav twice and pushed Register off
       the right edge at 1440. */
    { key: 'event',    label: 'Event',        Icon: IcoExhibit,  path: null,         menu: eventMenu,    scrollId: null        },
    { key: 'partners', label: 'Partners',     Icon: IcoPartners, path: '/partners',  menu: null,         scrollId: 'partners'  },
    { key: 'contact',  label: 'Contact',      Icon: IcoContact,  path: '/contact',   menu: null,         scrollId: 'contact'   },
];

const socialLinks = [
    { href: SOCIAL_LINKS.linkedin,  icon: faLinkedinIn, label: 'LinkedIn' },
    { href: SOCIAL_LINKS.instagram, icon: faInstagram,  label: 'Instagram' },
];

/* Dropdown menu */

const DropPanel = ({ items, visible, onSelect }) => (
    <div className={`ax-drop${visible ? ' ax-drop--open' : ''}`} role="menu">
        {items.map((item, i) => (
            <button
                key={item.id || item.label}
                className="ax-drop__row"
                style={{ '--i': i }}
                onClick={() => onSelect(item.id, item.path)}
                role="menuitem"
                type="button"
            >
                <span className="ax-drop__pip" aria-hidden />
                <span className="ax-drop__label">{item.label}</span>
                <span className="ax-drop__arrow" aria-hidden>›</span>
            </button>
        ))}
    </div>
);

/* -------------------------------------------------------------------------- */
/*  Main header                                                               */
/* -------------------------------------------------------------------------- */

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDrop, setOpenDrop] = useState(null);
    const [mobExpand, setMobExpand] = useState(null);
    const timers = useRef({});

    const location = useLocation();
    const navigate = useNavigate();

    /* scrolled state */
    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 20);
        fn();
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    /* lock body scroll when mobile is open */
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    /* close menus on route change */
    useEffect(() => {
        setMobileOpen(false);
        setOpenDrop(null);
        setMobExpand(null);
    }, [location.pathname]);

    /* close on escape */
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpenDrop(null);
                setMobileOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    /* hover open / close */
    const hover = (key) => {
        clearTimeout(timers.current[key]);
        setOpenDrop(key);
    };
    const unhover = (key) => {
        timers.current[key] = setTimeout(
            () => setOpenDrop((d) => (d === key ? null : d)),
            180
        );
    };

    /* cross-route navigation with scroll support */
    const goSection = useCallback(
        (scrollId, path) => {
            setMobileOpen(false);
            setOpenDrop(null);

            if (location.pathname !== path) {
                if (scrollId) sessionStorage.setItem('scrollToSection', scrollId);
                navigate(path);
                return;
            }
            
            if (scrollId) {
                const el = document.getElementById(scrollId);
                if (el) {
                    const h = document.querySelector('.site-header')?.offsetHeight || 80;
                    const offset = (document.querySelector('.ax-status')?.offsetHeight || 26) + h + 16;
                    window.scrollTo({
                        top: el.getBoundingClientRect().top + window.pageYOffset - offset,
                        behavior: 'smooth',
                    });
                }
            }
        },
        [location.pathname, navigate]
    );

    const isActive = ({ key, path, menu }) => {
        if (key === 'home') return location.pathname === '/';
        /* A chip that only opens a menu has no path of its own, so it marks
           itself active when the current route is one of its children —
           otherwise /workshops and /faq would leave nothing lit at all. */
        if (menu) return menu.some(m => m.path === location.pathname);
        return location.pathname === path;
    };

    return (
        <>
            {/* ============================================================ */}
            {/*  STATUS STRIP — institutional weight, editorial tone         */}
            {/* ============================================================ */}
            <div className="ax-status" aria-hidden="true">
                <div className="ax-status__left">
                    <span className="ax-status__dot" />
                    <span className="ax-status__tag">AI&nbsp;EXPO&nbsp;JORDAN&nbsp;·&nbsp;2026</span>
                    <span className="ax-status__sep" />
                    <span className="ax-status__marquee">
                        <b>Edition&nbsp;01</b>&nbsp;·&nbsp;1&nbsp;&amp;&nbsp;3&nbsp;October&nbsp;2026&nbsp;·&nbsp;UJ&nbsp;Academy
                    </span>
                </div>
                <div className="ax-status__right">
                    <span className="ax-status__tag ax-status__lang">EN&nbsp;/&nbsp;ع</span>
                </div>
            </div>

            {/* mobile-panel overlay */}
            {mobileOpen && (
                <div
                    className="ax-mobile__overlay ax-mobile__overlay--show"
                    onClick={() => setMobileOpen(false)}
                    aria-hidden
                />
            )}

            {/* ============================================================ */}
            {/*  MAIN HEADER                                                 */}
            {/* ============================================================ */}
            <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>

                {/*
                    LOGO WRAPPER — gets the brand-gradient shimmer overlay.
                    The shimmer is purely CSS — see .ax-logo::after in
                    Header.css. The image element stays as-is.
                */}
                <Link to="/" className="ax-logo" aria-label="AI Expo Jordan 2026 — Home">
                    <img
                        src="/images/logos/white-logo.webp"
                        alt="AI Expo Logo"
                        className="ax-logo__img"
                        decoding="async"
                        fetchPriority="high"
                    />
                </Link>

                {/* ---- NAV STRIP ---- */}
                <nav className="ax-nav" aria-label="Main navigation">
                    <div className="ax-nav__bg" aria-hidden />

                    {NAV.map((item, idx) => {
                        const active = isActive(item);
                        const dropped = openDrop === item.key;
                        const hasDrop = !!item.menu;
                        const wireHot = active || dropped;

                        return (
                            <React.Fragment key={item.key}>
                                {idx > 0 && (
                                    <div className={`ax-wire${wireHot ? ' ax-wire--hot' : ''}`} aria-hidden>
                                        <span className="ax-wire__line" />
                                        <span className="ax-wire__node" />
                                    </div>
                                )}

                                <div
                                    className="ax-seg"
                                    onMouseEnter={() => hasDrop && hover(item.key)}
                                    onMouseLeave={() => hasDrop && unhover(item.key)}
                                >
                                    {hasDrop ? (
                                        <button
                                            type="button"
                                            className={`ax-chip${active ? ' ax-chip--active' : ''}${dropped ? ' ax-chip--open' : ''}`}
                                            aria-haspopup="menu"
                                            aria-expanded={dropped}
                                            onClick={() =>
                                                setOpenDrop(d => d === item.key ? null : item.key)
                                            }
                                        >
                                            <span className="ax-chip__icon"><item.Icon /></span>
                                            <span className="ax-chip__label">{item.label}</span>
                                            <svg
                                                className={`ax-chip__chev${dropped ? ' ax-chip__chev--open' : ''}`}
                                                viewBox="0 0 10 6"
                                                fill="none"
                                                aria-hidden
                                            >
                                                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    ) : item.key === 'home' ? (
                                        <button
                                            type="button"
                                            className={`ax-chip${active ? ' ax-chip--active' : ''}`}
                                            onClick={() => goSection(item.scrollId, item.path)}
                                        >
                                            <span className="ax-chip__icon"><item.Icon /></span>
                                            <span className="ax-chip__label">{item.label}</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`ax-chip${active ? ' ax-chip--active' : ''}`}
                                            onClick={() => goSection(item.scrollId, item.path)}
                                        >
                                            <span className="ax-chip__icon"><item.Icon /></span>
                                            <span className="ax-chip__label">{item.label}</span>
                                        </button>
                                    )}

                                    {hasDrop && (
                                        <DropPanel
                                            items={item.menu}
                                            visible={dropped}
                                            onSelect={(scrollId, path) => goSection(scrollId, path)}
                                        />
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </nav>

                {/* ---- RIGHT RAIL ---- */}
                <div className="ax-right">
                    <div className="ax-socials">
                        {socialLinks.map(({ href, icon, label }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ax-social"
                                aria-label={label}
                            >
                                <FontAwesomeIcon icon={icon} />
                            </a>
                        ))}
                    </div>

                    {/* THE PURPLE MOMENT — register CTA */}
                    <Link
                        to="/program#track-selector"
                        className="ax-join"
                        onClick={() => setMobileOpen(false)}
                    >
                        <span className="ax-join__text">Register</span>
                        <span className="ax-join__arrow" aria-hidden>
                            <svg viewBox="0 0 16 10" fill="none">
                                <path d="M1 5h13m0 0L10 1m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </Link>

                    <button
                        type="button"
                        className={`ax-burger${mobileOpen ? ' ax-burger--open' : ''}`}
                        onClick={() => setMobileOpen(v => !v)}
                        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={mobileOpen}
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </header>

            {/* ============================================================ */}
            {/*  MOBILE PANEL                                                */}
            {/* ============================================================ */}
            <aside
                className={`ax-mobile${mobileOpen ? ' ax-mobile--open' : ''}`}
                aria-hidden={!mobileOpen}
            >
                <button
                    type="button"
                    className="ax-mobile__close"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close menu"
                >
                    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M1 1l12 12M13 1L1 13" />
                    </svg>
                </button>

                <div className="ax-mobile__top" aria-hidden>
                    <span>AI&nbsp;EXPO&nbsp;JORDAN&nbsp;·&nbsp;2026</span>
                    <span className="ax-mobile__live"><i />LIVE</span>
                </div>

                <ul className="ax-mobile__list">
                    {NAV.map((item, i) => (
                        <li key={item.key} className="ax-mobile__item" style={{ '--mi': i }}>
                            {item.menu ? (
                                <>
                                    <button
                                        type="button"
                                        className={`ax-mobile__link${isActive(item) ? ' ax-mobile__link--on' : ''}`}
                                        onClick={() => setMobExpand(v => v === item.key ? null : item.key)}
                                        aria-expanded={mobExpand === item.key}
                                    >
                                        <span className="ax-mobile__idx">0{i + 1}</span>
                                        <span className="ax-mobile__mico"><item.Icon /></span>
                                        <span className="ax-mobile__label">{item.label}</span>
                                        <svg
                                            className={`ax-mobile__chev${mobExpand === item.key ? ' ax-mobile__chev--open' : ''}`}
                                            viewBox="0 0 10 6"
                                            fill="none"
                                        >
                                            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                    <ul className={`ax-mobile__sub${mobExpand === item.key ? ' ax-mobile__sub--open' : ''}`}>
                                        {item.menu.map(m => (
                                            <li key={m.id}>
                                                <button
                                                    type="button"
                                                    className="ax-mobile__sublink"
                                                    onClick={() => goSection(m.id, m.path)}
                                                >
                                                    {m.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className={`ax-mobile__link${isActive(item) ? ' ax-mobile__link--on' : ''}`}
                                    onClick={() => goSection(item.scrollId, item.path)}
                                >
                                    <span className="ax-mobile__idx">0{i + 1}</span>
                                    <span className="ax-mobile__mico"><item.Icon /></span>
                                    <span className="ax-mobile__label">{item.label}</span>
                                </button>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="ax-mobile__foot">
                    <Link to="/program#track-selector" className="ax-join" onClick={() => setMobileOpen(false)}>
                        <span className="ax-join__text">Register</span>
                        <span className="ax-join__arrow" aria-hidden>
                            <svg viewBox="0 0 16 10" fill="none">
                                <path d="M1 5h13m0 0L10 1m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </Link>
                    <div className="ax-socials ax-socials--center">
                        {socialLinks.map(({ href, icon, label }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ax-social"
                                aria-label={label}
                            >
                                <FontAwesomeIcon icon={icon} />
                            </a>
                        ))}
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Header;